import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Slider } from "@/components/ui/slider";

interface CategoryOption {
  handle: string;
  title: string;
  image?: string;
}

interface SearchFiltersProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceRange: [number, number];
  setPriceRange: (range: [number, number]) => void;
  maxPrice: number;
  selectedCategory: string | null;
  setSelectedCategory: (handle: string | null) => void;
  availableCategories: CategoryOption[];
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  t: (key: string, fallback: string) => string;
}

/** Price range + category filters used inside SearchDialog. */
export const SearchFilters = ({
  open,
  onOpenChange,
  priceRange,
  setPriceRange,
  maxPrice,
  selectedCategory,
  setSelectedCategory,
  availableCategories,
  hasActiveFilters,
  onClearFilters,
  t,
}: SearchFiltersProps) => {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleContent className="space-y-4 pt-3 pb-2">
        <div className="bg-muted/30 rounded-lg p-3 sm:p-4 space-y-4">
          {/* Price Range */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t("priceRange", "טווח מחירים")}</span>
              <span className="text-xs text-muted-foreground">
                ₪{priceRange[0].toLocaleString()} - ₪{priceRange[1].toLocaleString()}
              </span>
            </div>
            <Slider
              value={priceRange}
              onValueChange={(value) => setPriceRange(value as [number, number])}
              min={0}
              max={Math.ceil(maxPrice)}
              step={50}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>₪0</span>
              <span>₪{Math.ceil(maxPrice).toLocaleString()}</span>
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-2">
            <span className="text-sm font-medium block">{t("category", "קטגוריה")}</span>
            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-3 py-1.5 rounded-full text-xs sm:text-sm border transition-all ${
                  selectedCategory === null
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background border-border hover:border-primary/50"
                }`}
              >
                {t("all", "הכל")}
              </button>
              {availableCategories.slice(0, 8).map((cat) => (
                <button
                  key={cat.handle}
                  onClick={() => setSelectedCategory(cat.handle)}
                  className={`px-3 py-1.5 rounded-full text-xs sm:text-sm border transition-all ${
                    selectedCategory === cat.handle
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-border hover:border-primary/50"
                  }`}
                >
                  {cat.title}
                </button>
              ))}
            </div>
          </div>

          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClearFilters}
              className="w-full sm:w-auto"
            >
              {t("clearFilter", "נקה סינון")}
            </Button>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
